<template>
  <div>
    <button type="button" class="dropzone form-control border" :class="grabbingPadding" :id="idString">
      <template v-if="maxFilesReached">
        <div class="alert alert-danger alert-dismissible small">
          {{$t("dropzone.max_files_exceeded")}}
          <button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
        </div>
      </template>
      <template v-if="fileAutoDeleted">
        <div class="alert alert-warning alert-dismissible small">
          {{$t("dropzone.max_files_reached_delete")}}
          <button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
        </div>
      </template>
    </button>
    <div :id="templateIdString" class="d-none">
      <div class="dz-preview dz-file-preview text-nowrap">
        <div class="dz-success-mark text-success">
          <svgicon name="check" view-box="0 0 16 16" />
        </div>
        <div class="dz-error-mark text-danger">
          <svgicon name="x" view-box="0 0 16 16" />
        </div>
        <div class="dz-image" :style="thumbnailStyle"><img data-dz-thumbnail /></div>
        <div class="dz-details">
          <div class="dz-filename">
            <span class="mx-1" data-dz-name></span>
            <div class="dz-size mx-1" data-dz-size></div>
          </div>
        </div>
        <div class="dz-progress border mx-1 my-0">
          <span class="dz-upload bg-primary bg-gradient" data-dz-uploadprogress></span>
        </div>
        <a data-dz-remove class="small text-danger d-flex align-items-center dz-remove">
          <svgicon name="trash" view-box="0 0 16 16" />
          <span class="dz-cancel-message">
            {{$t("dropzone.dict_cancel_upload")}}
          </span>
          <span class="dz-remove-message">
            {{$t("dropzone.dict_remove_file")}}
          </span>
        </a>
        <div class="dz-error-message">
          <span data-dz-errormessage></span>
        </div>
      </div>
    </div>
    <div v-if="maxFilesMessageVisibleValue || fileCountMessageVisibleValue"
         class="small text-secondary user-select-none d-flex flex-wrap justify-content-end">
      <span v-if="fileCountMessageVisibleValue">
        {{$t("dropzone.file_count_message", {fileCount: myDropzone?.files?.length})}}
      </span>
      <span v-if="maxFilesMessageVisibleValue" class="ms-2">
        {{$t("dropzone.max_files_message", {maxFiles: maxFilesValue})}}
      </span>
    </div>
  </div>
</template>

<script>
  import { defineComponent, nextTick } from "vue";
  import { xhrRequest, xhrErrors } from "utils/xhr";
  import { Dropzone } from "dropzone";
  import { jsonTryParse, deepCopy } from "utils/json";

  export default defineComponent({
    props: {
      idPrefix: {
        type: String,
        default: null,
      },
      url: {
        type: String,
        default: null,
      },
      paramName: {
        type: String,
        default: "file",
      },
      maxFiles: {
        type: Number,
        default: 1,
      },
      maxFilesMessageVisible: {
        type: Boolean,
        default: true,
      },
      fileCountMessageVisible: {
        type: Boolean,
        default: false,
      },
      maxFilesize: {
        type: Number,
        default: 256,
      },
      acceptedFiles: {
        type: String,
        default: "application/*,audio/*,video/*,image/*,text/*",
      },
      createImageThumbnails: {
        type: Boolean,
        default: false,
      },
      thumbnailsVisible: {
        type: Boolean,
        default: true,
      },
      thumbnailWidth: {
        type: Number,
        default: 120,
      },
      thumbnailHeight: {
        type: Number,
        default: 120,
      },
      autoQueue: {
        type: Boolean,
        default: true,
      },
      autoProcessQueue: {
        type: Boolean,
        default: true,
      },
      dictDefaultMessage: {
        type: String,
        default: null,
      },
      maxFilesExceededAction: {
        type: String,
        default: null,
        validator: (val) => {
          return [null, "pushout", "delete"].includes(val);
        },
      },
      maxFilesReachedToDisable: {
        type: Boolean,
        default: false,
      },
    },
    computed: {
      idString() {
        return `dropzone__container__${this.idPrefix}`;
      },
      templateIdString() {
        return `dropzone__preview__template__${this.idPrefix}`;
      },
      maxFilesValue() {
        if (this.maxFiles == null || !Number.isFinite(this.maxFiles)) {
          return 0;
        }
        return this.maxFiles;
      },
      maxFilesMessageVisibleValue() {
        if (this.maxFilesValue === 0) {
          return false;
        }
        return this.maxFilesMessageVisible;
      },
      dictDefaultMessageString() {
        if (this.dictDefaultMessage) {
          return this.dictDefaultMessage;
        } else {
          return this.$t("dropzone.dict_default_message");
        }
      },
      grabbingPadding() {
        return this.isGrabbing ? "py-5 dragging" : "py-2";
      },
      maxFilesReached() {
        return this.errorStatus.includes("maxFilesReached") && this.maxFilesReachedToDisable && this.maxFilesExceededAction !== "pushout";
      },
      fileAutoDeleted() {
        return this.errorStatus.includes("fileAutoDeleted");
      },
      thumbnailStyle() {
        return this.createImageThumbnails && this.thumbnailsVisible ? "" : "display: none";
      },
    },
    data: () => {
      return {
        myDropzone: null,
        dropzoneDOM: null,
        isGrabbing: false,
        timmerId: null,
        fileCountMessageVisibleValue: false,
        errorStatus: [],
      }
    },
    methods: {
      foldGrabbing() {
        this.isGrabbing = false;
        this.timmerId = null;
      },
      createMyDropzone() {
        if (this.myDropzone) {
          this.myDropzone.options.url = this.url;
          this.myDropzone.options.uploadMultiple = this.uploadMultipleEnable;
          this.myDropzone.options.maxFiles = this.maxFilesValue;
          this.myDropzone.options.maxFilesize = this.maxFilesize;
          this.myDropzone.options.acceptedFiles = this.acceptedFiles;
          this.myDropzone.options.createImageThumbnails = this.createImageThumbnails;
          this.myDropzone.options.thumbnailWidth = this.thumbnailWidth;
          this.myDropzone.options.thumbnailHeight = this.thumbnailHeight;
          this.myDropzone.options.thumbnailMethod = "contain";
          this.myDropzone.options.autoQueue = this.autoQueue;
          this.myDropzone.options.autoProcessQueue = this.autoProcessQueue;
          this.myDropzone.options.dictDefaultMessage = this.dictDefaultMessageString;
          return;
        }
        let csrfToken = document.getElementById("csrf-token");
        this.myDropzone = new Dropzone(`#${this.idString}`, {
          url: this.url,
          uploadMultiple: this.uploadMultipleEnable,
          maxFiles: this.maxFilesValue,
          maxFilesize: this.maxFilesize,
          acceptedFiles: this.acceptedFiles,
          createImageThumbnails: this.createImageThumbnails,
          thumbnailWidth: this.thumbnailWidth,
          thumbnailHeight: this.thumbnailHeight,
          thumbnailMethod: "contain",
          autoQueue: this.autoQueue,
          autoProcessQueue: this.autoProcessQueue,
          dictDefaultMessage: this.dictDefaultMessageString,
          dictFallbackMessage: this.$t("dropzone.dict_fallback_message"),
          dictFallbackText: this.$t("dropzone.dict_fallback_text"),
          dictFileTooBig: this.$t("dropzone.dict_file_too_big"),
          dictInvalidFileType: this.$t("dropzone.dict_invalid_file_type"),
          dictResponseError: this.$t("dropzone.dict_response_error"),
          dictCancelUpload: this.$t("dropzone.dict_cancel_upload"),
          dictUploadCanceled: this.$t("dropzone.dict_upload_canceled"),
          dictCancelUploadConfirmation: this.$t("dropzone.dict_cancel_upload_confirmation"),
          dictRemoveFile: this.$t("dropzone.dict_remove_file"),
          dictMaxFilesExceeded: this.$t("dropzone.dict_max_files_exceeded"),
          previewTemplate: document.getElementById(this.templateIdString).innerHTML,
          headers: {
            "X-CSRF-Token": csrfToken.getAttribute("content"),
          },
        });
      },
      handlingEvents() {
        this.myDropzone.on("paste", (e) => {
          this.$emit("paste", e);
        });
        this.myDropzone.on("reset", (file) => {
          this.$emit("reset", file);
        });
        this.myDropzone.on("addedfile", (file) => {
          this.$emit("addedfile", file);
          this.validateFile(file);
        });
        this.myDropzone.on("removedfile", (file) => {
          this.$emit("removedfile", file);
          this.validateFile();
        });
        this.myDropzone.on("thumbnail", (file, dataUrl) => {
          this.$emit("thumbnail", { file: file, dataUrl: dataUrl, });
        });
        this.myDropzone.on("error", (file, response, xhr) => {
          this.$emit("error", file);
          let em = file?.previewElement?.querySelector("[data-dz-errormessage]");
          if (em == null) return;
          if (xhr == null) return;
          let xhrJSON = jsonTryParse(xhr.response);
          if (xhrJSON == null || xhrJSON.errors == null || xhrJSON.errors.file == null || !Array.isArray(xhrJSON.errors.file)) {
            if (!Number.isFinite(xhr.status)) return;
            em.innerText = this.$t("dropzone.dict_response_error", { statusCode: xhr.status });
            return;
          };
          em.innerText = xhrJSON.errors.file[0];
        });
        this.myDropzone.on("errormultiple", () => {
          this.$emit("errormultiple");
        });
        this.myDropzone.on("processing", (file) => {
          this.$emit("processing", file);
        });
        this.myDropzone.on("processingmultiple", () => {
          this.$emit("processingmultiple");
        });
        this.myDropzone.on("uploadprogress", (file, progress) => {
          this.$emit("uploadprogress", { file: file, progress: progress, });
        });
        this.myDropzone.on("totaluploadprogress", (file, progress) => {
          this.$emit("totaluploadprogress", { file: file, progress: progress, });
        });
        this.myDropzone.on("sending", () => {
          this.$emit("sending");
        });
        this.myDropzone.on("sendingmultiple", () => {
          this.$emit("sendingmultiple");
        });
        this.myDropzone.on("success", (file) => {
          this.$emit("success", file);
        });
        this.myDropzone.on("successmultiple", () => {
          this.$emit("successmultiple");
        });
        this.myDropzone.on("canceled", (file) => {
          this.$emit("canceled", file);
        });
        this.myDropzone.on("canceledmultiple", () => {
          this.$emit("canceledmultiple");
        });
        this.myDropzone.on("complete", (file) => {
          this.$emit("complete", file);
        });
        this.myDropzone.on("maxfilesexceeded", () => {
          this.$emit("maxfilesexceeded");
        });
        this.myDropzone.on("maxfilesreached", () => {
          this.$emit("maxfilesreached");
        });
        this.myDropzone.on("queuecomplete", () => {
          this.$emit("queuecomplete");
        });
        this.myDropzone.on("addedfiles", () => {
          this.$emit("addedfiles");
        });
        document.addEventListener("dragover", (e) => {
          if (this.timmerId) clearTimeout(this.timmerId);
          this.isGrabbing = true;
          this.timmerId = setTimeout(this.foldGrabbing, 200);
        }, true);
      },
      validateFile() {
        this.fileCountMessageVisibleValue = this.myDropzone.files.length > 0 && this.fileCountMessageVisible;
        if (this.maxFilesValue <= this.myDropzone.files.length) {
          if (!this.errorStatus.includes("maxFilesReached")) this.errorStatus.push("maxFilesReached");
        } else{
          this.errorStatus = this.errorStatus.filter(i => i !== "maxFilesReached");
        }

        if (this.maxFilesExceededAction === "pushout" && this.maxFilesValue < this.myDropzone.files.length) {
          this.myDropzone.removeFile(this.myDropzone.files[0]);
          return;
        }

        if (this.maxFilesExceededAction === "delete" && this.maxFilesValue < this.myDropzone.files.length) {
          this.myDropzone.removeFile(this.myDropzone.getAddedFiles()[0]);
          if (!this.errorStatus.includes("fileAutoDeleted")) this.errorStatus.push("fileAutoDeleted");
          return;
        } else {
          this.errorStatus = this.errorStatus.filter(i => i !== "fileAutoDeleted");
        }
      },
      processQueue() {
        this.myDropzone.processQueue();
      },
      enqueueFile(index = 0) {
        let f = this.myDropzone.files?.filter(f => f.status === "added" && f.accepted === true)[index];
        if (f == null) return;
        this.myDropzone.enqueueFile(f);
      },
      enqueueFiles() {
        this.myDropzone.enqueueFiles(this.myDropzone.files?.filter(f => f.status === "added" && f.accepted === true));
      },
      openFileDialog() {
        if (this.myDropzone) {
          this.myDropzone.hiddenFileInput.click();
        }
      },
    },
    created() {
      Dropzone.autoDiscover = false;
    },
    mounted() {
      this.dropzoneDOM = document.getElementById(this.idString);

      this.createMyDropzone();

      this.handlingEvents();
    },
    emits: [
      "paste",
      "reset",
      "addedfile",
      "removedfile",
      "thumbnail",
      "error",
      "errormultiple",
      "processing",
      "processingmultiple",
      "uploadprogress",
      "totaluploadprogress",
      "sending",
      "sendingmultiple",
      "success",
      "successmultiple",
      "canceled",
      "canceledmultiple",
      "complete",
      "maxfilesexceeded",
      "maxfilesreached",
      "queuecomplete",
      "addedfiles",
    ],
    watch: {
      url: function () {
        this.createMyDropzone();
      },
      maxFiles: function () {
        this.createMyDropzone();
      },
      maxFilesValue: function () {
        this.createMyDropzone();
      },
      acceptedFiles: function () {
        this.createMyDropzone();
      },
      createImageThumbnails: function () {
        this.createMyDropzone();
      },
      autoQueue: function () {
        this.createMyDropzone();
      },
      autoProcessQueue: function () {
        this.createMyDropzone();
      },
      dictDefaultMessage: function () {
        this.createMyDropzone();
      },
    },
  });
</script>

<style>
  .dropzone {
    min-height: auto;
  }

  .dropzone.dragging {
    border-style: dashed !important;
  }

  .dropzone .dz-message {
    margin: auto;
    font-size: 0.85em;
  }

  .dropzone.dz-started:hover .dz-message {
    display: block;
  }

  .dropzone .dz-message .dz-button:hover {
    text-decoration: underline;
  }

  .dropzone .dz-preview {
    display: flex;
    align-items: center;
    margin: auto;
    min-height: auto;
  }

  .dropzone .dz-preview .dz-image {
    border-radius: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .dropzone .dz-preview .dz-image img,
  .dropzone .dz-preview:hover .dz-image img {
    filter: none;
  }

  .dropzone .dz-preview .dz-details {
    display: flex;
    position: static;
    padding: 0;
    min-width: 0;
    opacity: 1;
  }

  .dropzone .dz-preview.dz-error .dz-details {
    color: rgb(217, 83, 79);
  }

  .dropzone .dz-preview .dz-details .dz-size {
    margin-top: 0;
    margin-bottom: 0;
    font-size: 0.85em;
    white-space: nowrap;
  }

  .dropzone .dz-preview .dz-details .dz-filename:not(:hover) {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .dropzone .dz-preview .dz-details .dz-filename {
    display: flex;
    align-items: center;
    white-space: normal;
  }

  .dropzone .dz-preview .dz-details .dz-filename span,
  .dropzone .dz-preview .dz-details .dz-size span {
    padding: 0;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .dropzone .dz-preview .dz-details .dz-filename:hover span {
    word-break: break-all;
  }

  .dropzone .dz-preview .dz-success-mark,
  .dropzone .dz-preview .dz-error-mark {
    display: none;
    position: static;
    width: 1.5rem;
    min-width: 1.5rem;
    flex-basis: 1.5rem;
    margin: 0;
    opacity: 1;
    transition: none;
    animation: none !important;
  }

  .dropzone .dz-preview .dz-success-mark svg,
  .dropzone .dz-preview .dz-error-mark svg {
    width: 100%;
    height: 100%;
  }

  .dropzone .dz-preview.dz-error .dz-error-mark {
    display: block;
  }

  .dropzone .dz-preview:not(.dz-error) .dz-success-mark{
    display: block;
    visibility: hidden;
  }

  .dropzone .dz-preview.dz-success .dz-success-mark {
    visibility: visible;
  }

  .dropzone .dz-preview .dz-error-message {
    width: auto;
    white-space: normal;
    top: 100%;
  }

  .dropzone .dz-preview .dz-error-message:after {
    bottom: 100%;
    left: 50%;
  }

  .dropzone .dz-preview:not(.dz-complete) .dz-remove .dz-remove-message {
    display: none;
  }

  .dropzone .dz-preview.dz-complete .dz-remove .dz-cancel-message {
    display: none;
  }

  .dropzone .dz-preview .dz-progress {
    width: 5rem;
    height: 0.75rem;
    position: static;
  }

  .dropzone .dz-preview.dz-complete .dz-progress {
    display: none;
  }

  .dropzone .dz-preview:not(.dz-complete) .dz-progress {
    display: block;
  }

  .dropzone .dz-preview:not(.dz-processing) .dz-progress {
    animation: none;
  }

</style>
